import { env } from '@/src/config/env'
import { INTENTION_TYPE } from '@/src/features/intention/models/Intention.enums'
import { fetcher } from '@/src/general/utilities/fetcher'
import type { TSignalConfig } from '@/src/general/utilities/fetcher/IFetcher'
import { storage } from '@/src/general/utilities/storage'
import { STORAGE_KEYS } from '@/src/general/utilities/storage/MMKVStorage'
import { Cookie } from '@/src/general/utils/cookies/Cookie'

import type { TWizardPayload } from '../../settings/api/types/wizardApi.types'
import type { CONNECT_METHOD } from '../models/auth.enums'
import type { TCredentials, TMe } from '../models/auth.types'
import { authTokenStorage } from '../utils/authTokenStorage'
import { isTokenExpiringSoon } from '../utils/isTokenExpiringSoon'
import type { TMeApi } from './types/authApi.types'
import type { TGetLoginDetailsData } from './types/getLoginDetails.types'
import type { TMePayload } from './types/mePayload.types'
import type {
  TOtpAuthenticationData,
  TOtpCsrfData,
  TOtpInitializationPayload,
  TOtpLoginPayload,
  TOtpSignupPayload,
} from './types/otpAuthentication.types'
import type { TPostAppleLoginPayload } from './types/postAppleLogin.types'
import type { TPostGoogleLoginPayload } from './types/postGoogleLogin.types'
import type { TPostLogEmailPayload } from './types/postLogEmail.types'
import type { TPostPrescriberPayload } from './types/postPresubscriber.types'
import type { TPostResetPasswordData, TPostResetPasswordPayload } from './types/postResetPassword'
import type { TPostSignupData, TPostSignupPayload } from './types/postSignup.types'
import type { TIdTokenApi } from './types/tIdTokenApi'

export const meApiToMe = ({ intention, ...rest }: TMeApi): TMe => ({
  ...rest,
  intention: intention ? { ...intention, type: INTENTION_TYPE.INTENTION } : undefined,
  token: authTokenStorage.getToken() || '',
})

export const authApi = {
  refreshToken: (config: TSignalConfig) => fetcher.query<{ token: string }>('/token/refresh', config),

  async getMe(config: TSignalConfig) {
    const token = authTokenStorage.getToken() || ''
    fetcher.setXAuthToken(token)
    const meApi = await fetcher.query<TMeApi>('/me', config)
    if (isTokenExpiringSoon(token)) {
      authApi
        .refreshToken(config)
        .then(({ token: newToken }) => {
          fetcher.setXAuthToken(newToken)
          Cookie.set('jwtToken', newToken, 'oneYear')
          authTokenStorage.setToken(newToken)
        })
        .catch(() => {}) // background refresh; ignore failure, current token still valid
    }
    const me = meApiToMe(meApi)
    const { token: _token, ...storedMe } = me
    storage.setItem(STORAGE_KEYS.ME, JSON.stringify(storedMe))
    return me
  },

  postPresubscriber: (payload: TPostPrescriberPayload) => fetcher.mutate('/presubscriber', payload, { method: 'POST' }),

  getLoginMethods: (email: string, config: TSignalConfig) =>
    fetcher.query<CONNECT_METHOD[] | null>('/auth/login/methods', { params: { email }, ...config }),

  getLoginDetails: (params: { email?: string; emailHash?: string }, config: TSignalConfig) =>
    fetcher.query<TGetLoginDetailsData>('/auth/login/details', { params, ...config }),

  patchMe: (meUpdates: TMePayload) => fetcher.mutate<void, TMePayload>('/me', meUpdates, { method: 'PATCH' }),

  postLogEmail: (payload: TPostLogEmailPayload) =>
    fetcher.mutate<void, TPostLogEmailPayload>('/presubscriber', payload, { method: 'POST' }),

  postLogin: (credentials: TCredentials) =>
    fetcher.mutate<TIdTokenApi, TCredentials>('/login', credentials, { method: 'POST' }),

  getOtpLoginCsrf: (config: TSignalConfig = {}) =>
    fetcher.query<TOtpCsrfData>('/login/otp', { ...config, withCredentials: true }),

  initializeOtpLogin: (payload: TOtpInitializationPayload) =>
    fetcher.mutate<void, TOtpInitializationPayload>('/login/otp', payload, {
      method: 'POST',
      withCredentials: true,
    }),

  postOtpLogin: (payload: TOtpLoginPayload) =>
    fetcher.mutate<TOtpAuthenticationData, TOtpLoginPayload>('/login/otp', payload, {
      method: 'POST',
      withCredentials: true,
    }),

  getOtpSignupCsrf: (config: TSignalConfig = {}) =>
    fetcher.query<TOtpCsrfData>('/signup/otp', { ...config, withCredentials: true }),

  initializeOtpSignup: (payload: TOtpInitializationPayload) =>
    fetcher.mutate<void, TOtpInitializationPayload>('/signup/otp', payload, {
      method: 'POST',
      withCredentials: true,
    }),

  postOtpSignup: (payload: TOtpSignupPayload) =>
    fetcher.mutate<TOtpAuthenticationData, TOtpSignupPayload>('/signup/otp', payload, {
      method: 'POST',
      withCredentials: true,
    }),

  postAppleLogin: (payload: TPostAppleLoginPayload) =>
    fetcher.mutate<TIdTokenApi, TPostAppleLoginPayload>('/apple/auth', payload, { method: 'POST' }),

  postGoogleLogin: (payload: TPostGoogleLoginPayload) =>
    fetcher.mutate<TIdTokenApi, TPostGoogleLoginPayload>('/auth/app/callback', payload, { method: 'POST' }),

  postSignup: (payload: TPostSignupPayload) =>
    fetcher.mutate<TPostSignupData, TPostSignupPayload>('/signup', payload, {
      method: 'POST',
      params: {
        skipCaptcha:
          env.EXPO_PUBLIC_APP_BASE_URL &&
          ['localhost', '127.0.0.1', 'hozana.local'].includes(env.EXPO_PUBLIC_APP_BASE_URL),
      },
    }),

  deleteAccount: () => fetcher.mutate<void, undefined>('/me', undefined, { method: 'DELETE' }),

  patchWizard: (payload: TWizardPayload) =>
    fetcher.mutate<TMeApi, TWizardPayload>('/me/wizard', payload, { method: 'PATCH' }),

  requestPasswordRecovery: (email: string) =>
    fetcher.mutate<TNoMoreProps, { email: string }>('/users/reset-password', { email }, { method: 'POST' }),

  postResetPassword: (payload: TPostResetPasswordPayload) =>
    fetcher.mutate<TPostResetPasswordData, TPostResetPasswordPayload>('/users/reset-password/confirm', payload, {
      method: 'POST',
    }),
}
